<template>
  <layout-print-page>
    <div class="flex flex-between mb-100">
      <router-link :to="{ name: 'pipeline' }" class="flex flex-vertical-center">
        <img class="logo" src="/images/forefront-logo.png" alt="Forefront">
      </router-link>

      <div class="flex dont_print">
        <button-link @click="back" class="mr-100">Back</button-link>
        <button-outlined @click="print">Print</button-outlined>
      </div>
    </div>

    <div class="address">
      <icon-default class="address--icon" icon="lead" size="is-large"/>

      <p class="address--address">{{ property.line_1 }} {{ property.city }}, {{ property.state }} {{ property.postcode }}</p>
    </div>

    <div id="propertyDetails">
      <property-modal-section
        v-if="hasMidTierPropertyDataFeature"
        heading="Owner Information"
      >
        <property-modal-item
          label="Primary Owner"
          :value="ownerInfo.owner1FullName"
          column-class="is-6"/>
        <property-modal-item
          label="Secondary Owner"
          :value="ownerInfo.owner2FullName ? ownerInfo.owner2FullName : '--'"
          column-class="is-6"/>
        <property-modal-item
          label="Owner Type"
          :value="ownerInfo.owner1Type"
          column-class="is-6"/>
        <property-modal-item
          label="Ownership Length"
          :value="ownershipLength"
          column-class="is-6"/>
        <property-modal-item
          label="Mailing Address"
          :value="ownerInfo.mailAddress.label"
          column-class="is-12"/>
      </property-modal-section>

      <property-modal-section heading="General Details">
        <property-modal-item
          label="Bedrooms"
          :value="formatValue(property.bedrooms)"
          column-class="is-6"
        />
        <property-modal-item
          label="Stories"
          :value="formatValue(property.stories)"
          column-class="is-6"
        />
        <property-modal-item
          label="Basement Size"
          :value="formatValue(property.basement_size)"
          column-class="is-6"
        />
        <property-modal-item
          label="Bathrooms"
          :value="formatValue(property.bathrooms)"
          column-class="is-6"
        />
        <property-modal-item
          label="Parking Type"
          :value="formatValue(property.parking_type)"
          column-class="is-6"
        />
        <property-modal-item
          label="Construction Type"
          :value="formatValue(property.construction_type)"
          column-class="is-6"
        />
        <property-modal-item
          label="Partial Bathrooms"
          :value="formatValue(property.partial_bathrooms)"
          column-class="is-6"
        />
        <property-modal-item
          label="Pool"
          :value="formatBoolean(property.pool)"
          column-class="is-6"
        />
        <property-modal-item
          label="Architecture Type"
          :value="formatValue(property.architecture_type)"
          column-class="is-6"
        />
        <property-modal-item
          label="House Size"
          :value="formatValue(property.house_size)"
          column-class="is-6"
        />
        <property-modal-item
          label="Fireplaces"
          :value="formatValue(property.fireplaces)"
          column-class="is-6"
        />
        <property-modal-item
          label="Mobile Home"
          :value="formatBoolean(property.mobile_home)"
          column-class="is-6"
        />
        <property-modal-item
          label="Lot Size"
          :value="formatValue(property.lot_size)"
          column-class="is-6"
        />
        <property-modal-item
          label="Heating Type"
          :value="formatValue(property.heating_type)"
          column-class="is-6"
        />
        <property-modal-item
          label="Flood Zone"
          :value="formatBoolean(property.flood_zone)"
          column-class="is-6"
        />
        <property-modal-item
          label="Year Built"
          :value="formatValue(property.year_built)"
          column-class="is-6"
        />
        <property-modal-item
          label="Cooling Type"
          :value="formatValue(property.cooling_type)"
          column-class="is-6"
        />
        <property-modal-item
          label="Amenities"
          :value="formatValue(property.amenities)"
          column-class="is-12"
        />
      </property-modal-section>

      <property-modal-section heading="Assessment and Taxes">
        <property-modal-item label="Assessment Year" :value="taxInfo.assessmentYear" column-class="is-6"/>
        <property-modal-item label="Tax Year" :value="taxInfo.year" column-class="is-6"/>
        <property-modal-item label="Tax Amount" :value="formatCurrency(taxInfo.taxAmount)" column-class="is-6"/>
        <property-modal-item label="Assessed Value" :value="formatCurrency(taxInfo.assessedValue)" column-class="is-6"/>
        <property-modal-item label="Land Value" :value="formatCurrency(taxInfo.assessedLandValue)" column-class="is-6"/>
        <property-modal-item label="Improvement Value" :value="formatCurrency(taxInfo.assessedImprovementValue)" column-class="is-6"/>
        <property-modal-item label="Market Value" :value="formatCurrency(taxInfo.marketValue)" column-class="is-6"/>
        <property-modal-item label="Land Value" :value="formatCurrency(taxInfo.marketLandValue)" column-class="is-6"/>
        <property-modal-item label="Improvement Value" :value="formatCurrency(taxInfo.marketImprovementValue)" column-class="is-6"/>
      </property-modal-section>

      <property-modal-section
        v-if="hasMidTierPropertyDataFeature"
        heading="Geographic Information"
      >
        <property-modal-item label="APN" :value="geographicInfo.apn" column-class="is-12"/>
        <property-modal-item label="Legal Description" :value="geographicInfo.legalDescription" column-class="is-12"/>
        <property-modal-item label="Property Use" :value="geographicInfo.propertyUse" column-class="is-6"/>
        <property-modal-item label="Land Use" :value="geographicInfo.landUse" column-class="is-6"/>
        <property-modal-item label="Census Tract" :value="geographicInfo.censusTract" column-class="is-6"/>
        <property-modal-item label="Neighborhood" :value="geographicInfo.name" column-class="is-6"/>
        <property-modal-item label="Neighborhood Type" :value="geographicInfo.type" column-class="is-6"/>
        <property-modal-item label="Lot Number" :value="geographicInfo.lotNumber" column-class="is-6"/>
      </property-modal-section>

      <property-modal-section
        v-if="hasMidTierPropertyDataFeature"
        heading="Current Mortgage"
      >
        <property-modal-item
          label="Mortgage Balance"
          :value="formatCurrency(currentMortgage.openMortgageBalance)"
          column-class="is-6"
        />
        <property-modal-item
          label="Equity"
          :value="formatCurrency(currentMortgage.estimatedEquity)"
          column-class="is-6"
        />
        <property-modal-item
          label="High Equity"
          :value="formatBoolean(currentMortgage.highEquity)"
          column-class="is-6"
        />
        <property-modal-item
          label="Mortgage Payment"
          :value="formatCurrency(currentMortgage.estimatedMortgagePayment)"
          column-class="is-6"
        />
        <property-modal-item
          label="Equity Percent"
          :value="formatPercent(currentMortgage.equityPercent)"
          column-class="is-6"
        />
        <property-modal-item
          label="Free & Clear"
          :value="formatBoolean(currentMortgage.freeClear)"
          column-class="is-6"
        />
      </property-modal-section>

      <property-modal-wrapper
        v-if="hasAdvancedTierPropertyDataFeature"
        heading="Transaction History"
      >
        <template v-for="transaction in transactions">
          {{ /* sale */ }}
          <property-modal-section
            v-if="transaction.type === 'sale'"
            :heading="`Sale - ${formatDate(transaction.recordingDate)}`"
          >
            <property-modal-item
              label="Recording Date"
              :value="formatDate(transaction.recordingDate)"
              column-class="is-6"
            />
            <property-modal-item
              label="Sale Date"
              :value="formatDate(transaction.saleDate)"
              column-class="is-6"
            />
            <property-modal-item
              label="Sale Amount"
              :value="formatCurrency(transaction.saleAmount)"
              column-class="is-6"
            />
            <property-modal-item
              label="Down Payment"
              :value="formatCurrency(transaction.downPayment)"
              column-class="is-6"
            />
            <property-modal-item
              label="Sellers"
              :value="formatValue(transaction.sellerNames)"
              column-class="is-6"
            />
            <property-modal-item
              label="Purchase Method"
              :value="formatValue(transaction.purchaseMethod)"
              column-class="is-6"
            />
            <property-modal-item
              label="Buyers"
              :value="formatValue(transaction.buyerNames)"
              column-class="is-6"
            />
            <property-modal-item
              label="Transaction Type"
              :value="formatValue(transaction.transactionType)"
              column-class="is-6"
            />
            <property-modal-item
              label="Loan to Value"
              :value="formatPercent(transaction.ltv)"
              column-class="is-6"
            />
            <property-modal-item
              label="Document Type"
              :value="formatValue(transaction.documentType)"
              column-class="is-6"
            />
          </property-modal-section>

          {{ /* mortgage */ }}
          <property-modal-section
            v-if="transaction.type === 'mortgage'"
            :heading="`Mortgage - ${formatDate(transaction.recordingDate)}`"
          >
            <property-modal-item
              label="Recording Date"
              :value="formatDate(transaction.recordingDate)"

              column-class="is-
            6"
            />
            <property-modal-item
              label="Document Date"
              :value="formatDate(transaction.documentDate)"
              column-class="is-6"
            />
            <property-modal-item
              label="Amount"
              :value="formatCurrency(transaction.amount)"
              column-class="is-6"
            />
            <property-modal-item
              label="Loan Type"
              :value="formatValue(transaction.loanType)"
              column-class="is-6"
            />
            <property-modal-item
              label="Lender Name"
              :value="formatValue(transaction.lenderName)"
              column-class="is-6"
            />
            <property-modal-item
              label="Lender Type"
              :value="formatValue(transaction.lenderType)"
              column-class="is-6"
            />
            <property-modal-item
              label="Interest Rate"
              :value="formatPercent(transaction.interestRate)"
              column-class="is-6"
            />
            <property-modal-item
              label="Interest Rate Type"
              :value="formatValue(transaction.interestRateType)"
              column-class="is-6"
            />
            <property-modal-item
              label="Grantee Name"
              :value="formatValue(transaction.granteeName)"
              column-class="is-6"
            />
            <property-modal-item
              label="Transaction Type"
              :value="formatValue(transaction.transactionType)"
              column-class="is-6"
            />
            <property-modal-item
              label="Maturity Date"
              :value="formatDate(transaction.maturityDate)"
              column-class="is-6"
            />
            <property-modal-item
              label="Deed Type"
              :value="formatValue(transaction.deedType)"
              column-class="is-6"
            />
            <property-modal-item
              label="Term"
              :value="formatValue(transaction.term)"
              column-class="is-6"
            />
          </property-modal-section>
        </template>
      </property-modal-wrapper>

      <property-modal-wrapper
        v-if="hasAdvancedTierPropertyDataFeature"
        heading="School Information"
      >
        <property-modal-section
          v-for="(school, index) in schools"
          :key="index"
          :heading="'School - ' + school.name"
        >
          <property-modal-item
            label="Name"
            :value="formatValue(school.name)"
            column-class="is-12"
          />
          <property-modal-item
            label="Address"
            :value="formatValue(`${school.street}, ${school.city}, ${school.state} ${school.zip}`)"
            column-class="is-12"
          />
          <property-modal-item
            label="Grades"
            :value="formatValue(school.grades)"
          />
          <property-modal-item
            label="Type"
            :value="formatValue(school.type)"
          />
          <property-modal-item
            label="Enrollment"
            :value="formatValue(school.enrollment)"
          />
          <property-modal-item
            label="Rating"
            :value="formatValue(school.rating)"
          />
          <property-modal-item
            label="Parent Rating"
            :value="formatValue(school.parentRating)"
          />
        </property-modal-section>
      </property-modal-wrapper>
    </div>
  </layout-print-page>
</template>

<script>
import PropertyModalItem from "../lead/components/PropertyModalItem.vue";
import PropertyModalWrapper from "../lead/components/PropertyModalWrapper.vue";
import PropertyModalSection from "../lead/components/PropertyModalSection.vue";
import LayoutPrintPage from "../../layouts/page/LayoutPrintPage.vue";
import utils from "../../services/utils";
import dates from "../../services/dates";

export default {
  name: "PropertyPrintPage",
  metaInfo() {
    let property = this.property

    return {
      title: property.line_1
    }
  },
  components: {
    LayoutPrintPage,
    PropertyModalSection,
    PropertyModalWrapper,
    PropertyModalItem
  },
  computed: {
    hasMidTierPropertyDataFeature() {
      return this.$store.getters.account.has_mid_tier_property_data_feature;
    },
    hasAdvancedTierPropertyDataFeature() {
      return this.$store.getters.account.has_advanced_tier_property_data_feature;
    },
    property() {
      return this.$store.state.activeRecord.property
    },
    data() {
      if(this.showNoPropertyMessage) return null;
      return this.property.real_estate_property_data;
    },
    ownerInfo() {
      if(this.data) return this.data.ownerInfo;
      return null;
    },
    ownershipLength() {
      if(this.data) {
        const lengthInMonth = this.data.ownerInfo.ownershipLength
        return (lengthInMonth / 12 | 0) + " years / " + lengthInMonth % 12 +" months"
      }

      return null;
    },
    taxInfo() {
      if(this.data) return this.data.taxInfo;
      return null;
    },
    geographicInfo() {
      const data = this.data;
      if(this.data) return {...data.lotInfo, ...data.neighborhood};
      return null;
    },
    currentMortgage() {
      if(this.data) return this.data;
      return null;
    },
    transactions() {
      if(this.data) {
        const transactions = [];

        this.data.saleHistory.forEach((sale) => {
          transactions.push({
            type: 'sale',
            ...sale,
          })
        })

        this.data.mortgageHistory.forEach((mortgage) => {
          transactions.push({
            type: 'mortgage',
            ...mortgage,
          })
        })

        return transactions;
      }
      return null;
    },
    schools() {
      if(this.data) return this.data.schools;
      return null;
    }
  },
  methods: {
    back() {
      this.$router.push(this.$route.query.back_path)
    },
    print() {
      window.print()
    },
    formatValue(value) {
      if(!value) return '--'
      return value;
    },
    formatBoolean(value) {
      if (value === null || value === undefined) return '--'
      return value ? 'Yes' : 'No';
    },
    formatCurrency(value) {
      if(!value) return '--'
      return utils.formatCurrency(value)
    },
    formatDate(value) {
      if(!value) return '--'
      return dates.formatAsDate(value)
    },
    formatPercent(value) {
      if(!value) return '--'
      return value + '%';
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.logo {
  height: 28px;
}

.address {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding-left: 0.5rem;

  &--icon {
    margin-right: 0.5rem;
    color: $text-light;
  }

  &--address {
    font-size: 1.5rem;
  }
}

@media print {
  .dont_print {
    visibility: hidden;
  }
}
</style>
